// Teacher content review (docs/launch/TEACHER-REVIEW.md): metadata and helpers safe for the client.
// The sample itself (generated questions, hints, worked solutions) is built in the Worker
// (src/worker/contentReview.ts), the same way the course browser's questions are.
import type {Course} from './types';

// The young courses a teacher reviews, in grade order. 'early' (current foundations) is a placement
// helper, not a grade, so it is not on the list.
export const REVIEW_COURSES=[
  {course:'k4',grade:'Pre-K'},
  {course:'k5',grade:'Kindergarten'},
  {course:'grade1',grade:'Grade 1'},
  {course:'grade2',grade:'Grade 2'},
  {course:'grade3',grade:'Grade 3'},
  {course:'grade4',grade:'Grade 4'},
  {course:'grade5',grade:'Grade 5'},
] as const satisfies readonly {course:Course;grade:string}[];
export type ReviewCourse=typeof REVIEW_COURSES[number]['course'];
export const REVIEW_COURSE_IDS=REVIEW_COURSES.map(c=>c.course) as [ReviewCourse,...ReviewCourse[]];
export const reviewGrade=(course:string)=>REVIEW_COURSES.find(c=>c.course===course)?.grade??course;

export const REVIEW_KINDS=['story','card','question'] as const;
export type ReviewKind=typeof REVIEW_KINDS[number];
export const REVIEW_VERDICTS=['ok','fix'] as const;
export type ReviewVerdict=typeof REVIEW_VERDICTS[number];
export const REVIEW_CATEGORIES=[
  {id:'math',label:'Math error'},
  {id:'wording',label:'Confusing wording'},
  {id:'level',label:'Too hard or too easy for the grade'},
  {id:'visual',label:'Art or visual'},
  {id:'other',label:'Other'},
] as const;
export type ReviewCategory=typeof REVIEW_CATEGORIES[number]['id'];
export const REVIEW_CATEGORY_IDS=REVIEW_CATEGORIES.map(c=>c.id) as [ReviewCategory,...ReviewCategory[]];
export const reviewCategoryLabel=(id:string|null|undefined)=>REVIEW_CATEGORIES.find(c=>c.id===id)?.label??'';
export const REVIEW_NOTE_MAX=500;
// Two generated questions per topic.
export const REVIEW_QUESTIONS_PER_TOPIC=2;

// One reviewable item's address inside a course: kind plus key. Questions are keyed by learning point
// and seed ("q:<point>:<seed>"), cards by "card", stories by their story id.
export const reviewItemId=(kind:ReviewKind,topic:string,key:string)=>`${kind}|${topic}|${key}`;

// The deterministic sample: the same topic always gives the same questions, so two reviewers (and the
// owner opening a flagged item later) see the same thing. FNV-1a over course and topic.
export function reviewSeed(course:string,topic:string,index:number):number {
  let h=0x811c9dc5;
  for(const ch of `${course}:${topic}`){h^=ch.charCodeAt(0);h=Math.imul(h,0x01000193)>>>0;}
  return (h+index*7919)>>>0;
}
export const reviewQuestionKey=(point:number,seed:number)=>`q:${point}:${seed}`;
export function parseQuestionKey(key:string):{point:number;seed:number}|null {
  const m=/^q:(\d):(\d{1,10})$/.exec(key);
  if(!m)return null;
  const seed=Number(m[2]);
  return seed<=0xffffffff?{point:Number(m[1]),seed}:null;
}

// The Course browser link for an item, so the owner can open exactly what the reviewer saw.
export function reviewBrowserLink(course:string,topic:string,kind:ReviewKind,key:string):string {
  const q=kind==='question'?parseQuestionKey(key):null;
  return '/?'+new URLSearchParams({admin:'curriculum',course,topic,point:String(q?.point??0),sample:String(q?.seed??0),units:'us'}).toString();
}

// Stable fingerprint of what the reviewer saw (FNV-1a, hex). A verdict attests to this exact content.
export function reviewFingerprint(value:unknown):string {
  const text=JSON.stringify(value);
  let a=0x811c9dc5,b=0x01000193^text.length;
  for(let i=0;i<text.length;i++){const c=text.charCodeAt(i);a^=c;a=Math.imul(a,0x01000193)>>>0;b^=c;b=Math.imul(b,0x5bd1e995)>>>0;}
  return a.toString(16).padStart(8,'0')+b.toString(16).padStart(8,'0');
}

export interface ReviewOutlineItem {id:string;kind:ReviewKind;topic:string;key:string}
export interface ReviewOutlineTopic {id:string;name:string;unit:string;items:ReviewOutlineItem[]}
export interface ReviewVerdictView {kind:ReviewKind;topic:string;key:string;verdict:ReviewVerdict;category:ReviewCategory|null;note:string|null;contentVersion:string;stale:boolean;updatedAt:string}
export interface ReviewGradeSummary {course:ReviewCourse;grade:string;name:string;total:number;reviewed:number;needsFix:number;stale:number}
export interface ReviewOutline {contentVersion:string;course:ReviewCourse;grade:string;name:string;total:number;reviewed:number;topics:ReviewOutlineTopic[];verdicts:ReviewVerdictView[]}

// The review's place in the address bar (?admin=review&course=…&topic=…), dropped when leaving the tab.
export function leaveReviewQuery(search:string) {
  const p=new URLSearchParams(search);
  if(p.get('admin')==='review')for(const key of ['admin','course','topic'])p.delete(key);
  return p.toString();
}
